import clsx from 'clsx'

import { GameIcon } from '@/shared/ui/GameIcon'

export type RewardPillType = 'coins' | 'xp'

type Props = {
  type?: RewardPillType
  value: number
  className?: string
  compact?: boolean
  block?: boolean
}

const PILL_STYLES: Record<
  RewardPillType,
  { border: string; surface: string; text: string; icon: 'coins' | 'star' }
> = {
  coins: {
    border: 'border-[var(--color-reward-pill-coins-border,#edd9b8)]',
    surface: 'bg-[var(--color-reward-pill-coins-surface,#fff8f0)]',
    text: 'text-[var(--color-metric-coins)]',
    icon: 'coins'
  },
  xp: {
    border: 'border-[var(--color-reward-pill-xp-border)]',
    surface: 'bg-[var(--color-reward-pill-xp-surface)]',
    text: 'text-[var(--color-metric-points)]',
    icon: 'star'
  }
}

/**
 * Pill compacto de recompensa (+N) — rail direito de cards (objetivos, programação).
 * Superfície clara + borda tintada; sem rótulo textual (diferente de `RewardBadge`).
 *
 * Use `RewardPill.Group` quando xp e moedas aparecem juntos no mesmo rail.
 */
export const RewardPill = ({
  type = 'coins',
  value,
  className,
  compact = false,
  block = true
}: Props) => {
  if (value <= 0) return null

  const style = PILL_STYLES[type]

  return (
    <div
      className={clsx(
        'inline-flex items-center justify-center gap-0.5 rounded-md border',
        style.border,
        style.surface,
        style.text,
        compact ? 'px-1 py-0.5' : block && 'w-full gap-1 rounded-lg px-1.5 py-1.5',
        !compact && !block && 'rounded-lg px-1.5 py-1.5',
        className
      )}
    >
      <GameIcon
        name={style.icon}
        size={compact ? 12 : 14}
        className="shrink-0"
        aria-hidden
      />
      <span
        className={clsx(
          'font-bold leading-none tabular-nums',
          compact ? 'text-2xs' : 'text-xs'
        )}
      >
        +{value}
      </span>
    </div>
  )
}

export type RewardPillGroupProps = {
  points?: number | null
  coins?: number | null
  className?: string
  compact?: boolean
  /** Coluna (rail 70px) ou linha (preview compacto). */
  direction?: 'column' | 'row'
}

const RewardPillGroup = ({
  points = 0,
  coins = 0,
  className,
  compact = false,
  direction = 'column'
}: RewardPillGroupProps) => {
  const pointValue = points ?? 0
  const coinValue = coins ?? 0

  if (pointValue <= 0 && coinValue <= 0) return null

  const pillBlock = direction === 'column'

  return (
    <div
      className={clsx(
        'flex w-full gap-1',
        direction === 'row' ? 'flex-row flex-wrap items-center' : 'flex-col items-stretch',
        className
      )}
    >
      {pointValue > 0 && (
        <RewardPill
          type="xp"
          value={pointValue}
          compact={compact}
          block={pillBlock}
        />
      )}
      {coinValue > 0 && (
        <RewardPill
          type="coins"
          value={coinValue}
          compact={compact}
          block={pillBlock}
        />
      )}
    </div>
  )
}

RewardPillGroup.displayName = 'RewardPillGroup'

RewardPill.Group = RewardPillGroup
RewardPill.displayName = 'RewardPill'
